import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_URL, MAX_SUGGESTIONS, MAX_MESSAGE_LENGTH, WARNING_THRESHOLD, ERROR_THRESHOLD, AUTO_SCROLL_DELAY } from '../utils/config';
import { renderMarkdown } from '../utils/markdown';
import { formatTime } from '../utils/formatTime';
import {
  getSessionId,
  saveMessageHistory,
  loadMessageHistory,
  clearMessageHistory,
  generateNewSessionId,
  loadRecentChats,
  saveRecentChats,
  saveSessionSnapshot,
  loadSessionSnapshot,
  removeSessionSnapshot,
  setStoredSessionId,
  saveLastKpiQuestion,
  buildKpiHistory
} from '../utils/storage';
import authService from '../utils/auth.js';
import { connectNovaErp } from '../services/novaErpIntegrationApi.js';
import { getApiBaseUrl } from '../utils/apiBase';
import KnowledgeUpload from './KnowledgeUpload';
import TrialBanner from './TrialBanner';
import SubscriptionPaywall from './SubscriptionPaywall.jsx';
import { useDeviceDetect } from '../utils/useDeviceDetect';
import {
  FiAlertCircle, FiArrowUpRight, FiBriefcase, FiCalendar, FiCheck, FiCheckCircle, FiChevronDown, FiChevronRight, FiChevronUp, FiCopy,
  FiCpu, FiCreditCard, FiDollarSign, FiFileText, FiGrid, FiHeadphones, FiHeart, FiLock,
  FiMessageSquare, FiMoreHorizontal, FiPlus, FiRefreshCw, FiStar, FiThumbsDown, FiThumbsUp, FiTrash2, FiX,
} from 'react-icons/fi';
import TourMode from './tour/TourMode.jsx';
import TourButton from './tour/TourButton.jsx';
import { usePageTour } from './tour/usePageTour';
import ThemeToggle from './ThemeToggle.jsx';
import NovaBotXLogo from './NovaBotXLogo.jsx';
import { confirmDialog } from './ConfirmDialog.jsx';
import HelpMenu, { FeedbackButton } from './help/HelpMenu.jsx';
import HelpCenter from './help/HelpCenter.jsx';
import '../styles/chat.css';

// Guided tour of the chat workspace. `target` matches a data-tour attribute.
const CHAT_TOUR_STEPS = [
  { target: 'chat-new', title: 'Start a new conversation', body: 'Begin a fresh chat any time. Each conversation is saved so you can come back to it.' },
  { target: 'chat-recent', title: 'Your recent chats', body: 'Jump back into earlier conversations, or delete ones you no longer need.' },
  { target: 'chat-quick', title: 'Quick actions', body: 'One-click questions for common topics like leave, benefits, expenses and payroll.' },
  { target: 'chat-messages', title: 'Answers from your knowledge base', body: 'NovaBotX™ answers from your company’s documents and sites. Copy, rate or retry any answer, and tap a suggestion to keep going.' },
  { target: 'chat-composer', title: 'Ask anything', body: 'Type a question in plain language and press Enter. To match a résumé to a job, attach it, paste the job description and end with #novamatch.' },
];

const API_BASE_URL = getApiBaseUrl();

function deriveChatTitle(messages) {
  const userMsg = messages.find((m) => m.sender === 'user');
  if (userMsg) {
    const text = userMsg.content.trim();
    return text.length > 42 ? `${text.slice(0, 42)}…` : text;
  }
  return 'New chat';
}

const EMPTY_CHAT_TITLE = 'New chat';

// The model tags answers like "[TOPIC: HR]" for the server; never show those to users.
function stripTopicTags(text) {
  return String(text || '').replace(/\s*\[TOPIC:\s*[A-Z_]+\]/g, '');
}

// Short "when" label for the recent-chats list.
function formatRecentTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getUserInitials(user) {
  if (!user?.email) return '?';
  const local = user.email.split('@')[0];
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function getUserDisplayName(user) {
  if (!user?.email) return 'User';
  return user.email.split('@')[0];
}

// "yashwanth.setty@…" → "Yashwanth" when the profile has no first name.
function getFirstName(user) {
  const raw = user?.firstName || user?.email?.split('@')[0].split(/[._-]+/)[0] || '';
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

// Starter prompts on the empty-chat welcome screen.
const STARTER_PROMPTS = [
  { icon: FiCalendar, title: 'Time off', question: 'What is our leave policy?' },
  { icon: FiHeart, title: 'Benefits', question: 'What benefits are offered?' },
  { icon: FiCreditCard, title: 'Expenses', question: 'How do I submit expenses?' },
  { icon: FiBriefcase, title: 'Payroll', question: 'Who do I contact for payroll?' },
];

function formatRoleLabel(role) {
  if (!role) return 'User';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [lastUserMessage, setLastUserMessage] = useState(null);
  const [sessionId, setSessionId] = useState(() => getSessionId());
  const [showFeedback, setShowFeedback] = useState(false);
  const [helpCenterTab, setHelpCenterTab] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [novamatchResume, setNovamatchResume] = useState(null);
  const [starRating, setStarRating] = useState(0);
  const [feedbackStatus, setFeedbackStatus] = useState(null); // { type: 'error' | 'sent' | 'sending', message }
  const [reviewText, setReviewText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState(undefined); // undefined = not loaded yet, null = loaded but no custom message
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [hrContactEmail, setHrContactEmail] = useState(null); // null = not configured, string = configured email
  const [financeEmail, setFinanceEmail] = useState(null); // null = not configured, string = configured email
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null); // null = use default, string = custom logo URL
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSubscriptionPaywall, setShowSubscriptionPaywall] = useState(false);
  const [recentChats, setRecentChats] = useState(() => loadRecentChats());
  const [showQuickActionsExpanded, setShowQuickActionsExpanded] = useState(false);
  const [showAllRecentChats, setShowAllRecentChats] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const trialInfo = currentUser?.trial || null;
  const chatEntitlementBlocked =
    Boolean(currentUser && currentUser.canUseChat === false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const novamatchResumeInputRef = useRef(null);
  const welcomeMessageShownRef = useRef(false); // Track if welcome message has been shown to prevent duplicates
  
  // Device detection
  const { isMobile, isTablet, isTouchDevice } = useDeviceDetect();

  // Tour mode: opens only from the header Tour button
  const { touring, start: beginTour, end: endTour } = usePageTour();

  // Load tenants for superadmin
  const loadTenants = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant`, {
        headers: authService.getAuthHeaders()
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.tenants) {
        setTenants(data.data.tenants);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
    }
  };

  // Load current user profile
  const loadCurrentUser = async () => {
    try {
      const profile = await authService.getProfile();
      const user = profile.data?.user || profile.user;
      if (user && user.email) {
        setCurrentUser(user);
      } else {
        // Try to get user from token as fallback
        try {
          const token = authService.getToken();
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.email) {
              setCurrentUser({ email: payload.email, role: payload.role || 'user' });
            }
          }
        } catch (tokenErr) {
          console.error('ChatPage - Failed to extract user from token:', tokenErr);
        }
      }
    } catch (err) {
      console.error('ChatPage - Failed to load current user:', err);
      // Try to get user from token as fallback
      try {
        const token = authService.getToken();
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.email) {
            setCurrentUser({ email: payload.email, role: payload.role || 'user' });
          }
        }
      } catch (tokenErr) {
        console.error('ChatPage - Failed to extract user from token:', tokenErr);
      }
    }
  };

  // Open pricing when backend reports paywall (expired external trial)
  useEffect(() => {
    if (
      currentUser?.requiresPaywall &&
      currentUser?.canUseChat === false &&
      currentUser?.tenantBypassSubscription !== true
    ) {
      setShowSubscriptionPaywall(true);
    }
  }, [currentUser?.requiresPaywall, currentUser?.canUseChat, currentUser?.tenantBypassSubscription]);

  // Check admin and superadmin status on mount
  useEffect(() => {
    const checkAdmin = async () => {
      // Always verify with backend to get current role (in case role was updated)
      try {
        const [adminStatus, superAdminStatus] = await Promise.all([
          authService.checkAdminStatus(),
          authService.checkSuperAdminStatus()
        ]);
        setIsAdmin(adminStatus);
        setIsSuperAdmin(superAdminStatus);
        
        // Load tenants if superadmin
        if (superAdminStatus) {
          loadTenants();
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        // Fallback to token check if backend check fails
        const tokenAdmin = authService.isAdmin();
        setIsAdmin(tokenAdmin);
        setIsSuperAdmin(false);
      }
    };
    checkAdmin();
    loadCurrentUser();
  }, []);

  const addMessage = (content, sender) => {
    const messageId = Date.now();
    const timestamp = new Date().toISOString();
    const newMessage = {
      id: messageId,
      content,
      sender,
      timestamp
    };
    setMessages(prev => {
      const updated = [...prev, newMessage];
      saveMessageHistory(updated);
      return updated;
    });
    return newMessage;
  };

  const showWelcomeMessage = useCallback(() => {
    const message = welcomeMessage || "Hi! I'm NovaBot, your AI Assistant. I can help you with company policies, benefits, PTO requests, and more. What can I help you with today?";
    addMessage(message, 'bot');
    setSuggestions([
      'What is our leave policy?',
      'How do I submit expenses?',
      'Who do I contact for payroll?'
    ].slice(0, MAX_SUGGESTIONS));
  }, [welcomeMessage]);

  // Fetch welcome message function
  const fetchWelcomeMessage = useCallback(async () => {
    try {
      // Add cache-busting timestamp to ensure we get the latest message
      const timestamp = new Date().getTime();
      const url = `${API_BASE_URL}/api/tenant/welcome-message${isSuperAdmin && selectedTenant ? `?companyId=${selectedTenant}&_t=${timestamp}` : `?_t=${timestamp}`}`;
      const response = await fetch(url, {
        headers: authService.getAuthHeaders(),
        cache: 'no-cache' // Ensure we don't use cached responses
      });
      const data = await response.json();
      if (data.status === 'success') {
        // Backend now returns null if no custom message, or the message if set
        const fetchedMessage = data.data?.welcomeMessage;
        if (fetchedMessage) {
          setWelcomeMessage(fetchedMessage);
        } else {
          setWelcomeMessage(null);
        }
      } else {
        setWelcomeMessage(null);
      }
    } catch (err) {
      console.error('Error fetching welcome message:', err);
      // Use default message if fetch fails - set to null to indicate we've tried loading
      setWelcomeMessage(null);
    }
  }, [isSuperAdmin, selectedTenant]);

  // Fetch contact emails function
  const fetchContactEmails = useCallback(async () => {
    try {
      const timestamp = new Date().getTime();
      const url = `${API_BASE_URL}/api/tenant/contact-emails${isSuperAdmin && selectedTenant ? `?companyId=${selectedTenant}&_t=${timestamp}` : `?_t=${timestamp}`}`;
      const response = await fetch(url, {
        headers: authService.getAuthHeaders(),
        cache: 'no-cache'
      });
      const data = await response.json();
      if (data.status === 'success') {
        const hrEmail = data.data?.hrContactEmail;
        setHrContactEmail(hrEmail || null);
        const financeEmailFromSettings = data.data?.financeContactEmail || data.data?.tenant?.settings?.financeContactEmail;
        setFinanceEmail(financeEmailFromSettings || null);
      } else {
        setHrContactEmail(null);
        setFinanceEmail(null);
      }
    } catch (err) {
      console.error('Error fetching contact emails:', err);
      setHrContactEmail(null);
      setFinanceEmail(null);
    }
  }, [isSuperAdmin, selectedTenant]);

  // Fetch tenant logo function
  const fetchTenantLogo = useCallback(async () => {
    try {
      // Determine companyId - use selectedTenant for superadmin, currentUser.companyId for regular users
      let companyId = null;
      if (isSuperAdmin && selectedTenant) {
        companyId = selectedTenant;
      } else if (currentUser?.companyId) {
        companyId = currentUser.companyId;
      }

      if (!companyId) {
        setTenantLogoUrl(null);
        return;
      }

      const timestamp = new Date().getTime();
      const url = `${API_BASE_URL}/api/tenant/${companyId}?_t=${timestamp}`;
      const response = await fetch(url, {
        headers: authService.getAuthHeaders(),
        cache: 'no-cache'
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.tenant) {
        const logoUrl = data.data.tenant.settings?.customBranding?.logoUrl;
        if (logoUrl) {
          setTenantLogoUrl(logoUrl);
        } else {
          setTenantLogoUrl(null);
        }
      } else {
        setTenantLogoUrl(null);
      }
    } catch (err) {
      console.error('Error fetching tenant logo:', err);
      setTenantLogoUrl(null);
    }
  }, [isSuperAdmin, selectedTenant, currentUser]);

  // Fetch welcome message on mount and when tenant changes
  useEffect(() => {
    fetchWelcomeMessage();
  }, [fetchWelcomeMessage]);

  // Fetch contact emails on mount and when tenant changes
  useEffect(() => {
    fetchContactEmails();
  }, [fetchContactEmails]);

  // Fetch tenant logo on mount and when tenant/user changes
  useEffect(() => {
    fetchTenantLogo();
  }, [fetchTenantLogo]);

  // Listen for logo update events from AdminDashboard
  useEffect(() => {
    const handleLogoUpdate = (event) => {
      // Only update if the logo is for the current tenant
      const eventCompanyId = event.detail?.companyId;
      const currentCompanyId = isSuperAdmin && selectedTenant ? selectedTenant : currentUser?.companyId;
      
      if (eventCompanyId === currentCompanyId) {
        setTenantLogoUrl(event.detail?.logoUrl || null);
      }
    };

    window.addEventListener('tenant-logo-updated', handleLogoUpdate);
    return () => {
      window.removeEventListener('tenant-logo-updated', handleLogoUpdate);
    };
  }, [isSuperAdmin, selectedTenant, currentUser]);

  const escalateToFinance = () => {
    if (!financeEmail) {
      alert('Finance contact email is not configured. Please contact your administrator.');
      return;
    }
    const subject = encodeURIComponent('Assistance Request from NovaBot');
    const body = encodeURIComponent('Hi Finance Team,\n\nI need assistance with a finance question that NovaBot could not resolve.\n\nThanks,');
    const mailto = `mailto:${financeEmail}?subject=${subject}&body=${body}`;
    window.open(mailto, '_blank');
  };

  // Refetch welcome message when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refetch welcome message when user returns to the page
        fetchWelcomeMessage();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchWelcomeMessage]);

  // Listen for welcome message and contact emails updates from admin dashboard
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'welcome-message-updated') {
        fetchWelcomeMessage();
        // Clear the flag
        localStorage.removeItem('welcome-message-updated');
      } else if (e.key === 'contact-emails-updated') {
        fetchContactEmails();
        // Clear the flag
        localStorage.removeItem('contact-emails-updated');
      }
    };

    const handleWelcomeMessageEvent = () => {
      fetchWelcomeMessage();
      localStorage.removeItem('welcome-message-updated');
    };

    const handleContactEmailsEvent = () => {
      fetchContactEmails();
      localStorage.removeItem('contact-emails-updated');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('welcome-message-updated', handleWelcomeMessageEvent);
    window.addEventListener('contact-emails-updated', handleContactEmailsEvent);
    
    // Also check for same-tab updates (using polling as fallback)
    const checkInterval = setInterval(() => {
      if (localStorage.getItem('welcome-message-updated')) {
        fetchWelcomeMessage();
        localStorage.removeItem('welcome-message-updated');
      }
      if (localStorage.getItem('contact-emails-updated')) {
        fetchContactEmails();
        localStorage.removeItem('contact-emails-updated');
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('welcome-message-updated', handleWelcomeMessageEvent);
      window.removeEventListener('contact-emails-updated', handleContactEmailsEvent);
      clearInterval(checkInterval);
    };
  }, [fetchWelcomeMessage, fetchContactEmails]);

  // Load chat history on mount (only once)
  useEffect(() => {
    const savedMessages = loadMessageHistory();
    if (savedMessages.length > 0) {
      setMessages(savedMessages);
      // If messages already exist, mark welcome message as shown to prevent duplicates
      const hasWelcomeMessage = savedMessages.some(msg => msg.sender === 'bot');
      if (hasWelcomeMessage) {
        welcomeMessageShownRef.current = true;
      }
    }
    // Welcome message will be shown by the other useEffect when it's loaded
  }, []); // Only run once on mount

  // When welcome message updates, update it in the chat if it's the first message
  useEffect(() => {
    if (welcomeMessage === undefined) return; // Don't do anything if still loading
    
    const currentWelcomeText = welcomeMessage || "Hi! I'm NovaBot, your AI Assistant. I can help you with company policies, benefits, PTO requests, and more. What can I help you with today?";
    
    // Check if welcome message already exists in messages FIRST (before any other logic)
    const botMessages = messages.filter(msg => msg.sender === 'bot');
    const hasWelcomeMessage = botMessages.length > 0;
    
    // If welcome message already exists, mark flag and return early to prevent duplicates
    if (hasWelcomeMessage) {
      welcomeMessageShownRef.current = true;
      // If there's exactly one bot message, check if we need to update its content
      if (botMessages.length === 1) {
        const firstBotMessage = botMessages[0];
        if (firstBotMessage && firstBotMessage.content !== currentWelcomeText) {
          const updatedMessage = {
            ...firstBotMessage,
            content: currentWelcomeText
          };
          // Update the message in the array
          const updatedMessages = messages.map(msg => 
            msg.id === firstBotMessage.id ? updatedMessage : msg
          );
          setMessages(updatedMessages);
          saveMessageHistory(updatedMessages);
        }
      }
      return; // Don't add another welcome message
    }
    
    // Reset flag when messages are cleared (for when user clears chat)
    if (messages.length === 0) {
      welcomeMessageShownRef.current = false;
    }
    
    // If chat is empty and flag is not set, show welcome message (but only once)
    // Use a guard to prevent multiple simultaneous adds
    if (messages.length === 0 && !welcomeMessageShownRef.current) {
      // Set flag IMMEDIATELY before adding message to prevent race condition
      welcomeMessageShownRef.current = true;
      // Use the current welcome message directly
      const message = currentWelcomeText;
      addMessage(message, 'bot');
      setSuggestions([
        'What is our leave policy?',
        'How do I submit expenses?',
        'Who do I contact for payroll?'
      ].slice(0, MAX_SUGGESTIONS));
      return;
    }
  }, [welcomeMessage, messages]);


  // Auto-scroll to bottom when messages change - DISABLED
  // Users can manually scroll to read messages without interruption
  // useEffect(() => {
  //   scrollToBottom();
  // }, [messages, isTyping]);

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      });
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, AUTO_SCROLL_DELAY);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_MESSAGE_LENGTH) {
      setInputValue(value);
      autoResizeTextarea();
    }
  };

  const autoResizeTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isNovaMatchCommand = (text) => /#novamatch\b/i.test(text || '');

  const runNovaMatchAnalysis = async (messageText) => {
    if (chatEntitlementBlocked) {
      setShowSubscriptionPaywall(true);
      return;
    }

    const jdFromMessage = messageText.replace(/#novamatch\b/gi, '').trim();
    if (!novamatchResume) {
      addMessage(messageText, 'user');
      setInputValue('');
      addMessage(
        'To run **#novamatch**, attach a **resume**, paste the **job description** in the chat box, and end with `#novamatch`.',
        'bot'
      );
      return;
    }
    if (!jdFromMessage) {
      addMessage(messageText, 'user');
      setInputValue('');
      addMessage(
        'Paste the **job description** in the chat box and put `#novamatch` at the end, then send.',
        'bot'
      );
      return;
    }

    setLastUserMessage(messageText);
    addMessage(messageText, 'user');
    setInputValue('');
    setSuggestions([]);
    setIsLoading(true);
    setIsTyping(true);

    const botMessageId = Date.now() + 1;
    const botMessage = {
      id: botMessageId,
      content: 'Running NovaMatch analysis… this can take up to a couple of minutes.',
      sender: 'bot',
      timestamp: new Date().toISOString(),
      streaming: true
    };
    setMessages((prev) => [...prev, botMessage]);

    try {
      const form = new FormData();
      form.append('resume', novamatchResume);
      form.append('jobDescription', jdFromMessage);

      const headers = { ...authService.getAuthHeaders() };
      delete headers['Content-Type'];

      const response = await fetch(`${API_BASE_URL}/api/novamatch/analyze`, {
        method: 'POST',
        headers,
        body: form
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (data?.code === 'SUBSCRIPTION_REQUIRED' || data?.requiresPaywall) {
          setShowSubscriptionPaywall(true);
        }
        throw new Error(data.message || `NovaMatch failed (${response.status})`);
      }

      const downloadPath = data.downloadUrl
        ? `${API_BASE_URL}${data.downloadUrl}`
        : null;

      setMessages((prev) => {
        const updated = prev.map((msg) =>
          msg.id === botMessageId
            ? {
                ...msg,
                content: data.markdown || 'Analysis complete.',
                streaming: false,
                novamatchDownloadUrl: downloadPath,
                novamatchHasPdf: Boolean(data.hasPdf)
              }
            : msg
        );
        saveMessageHistory(updated);
        return updated;
      });

      setNovamatchResume(null);
      if (novamatchResumeInputRef.current) novamatchResumeInputRef.current.value = '';
    } catch (err) {
      console.error('NovaMatch error:', err);
      setMessages((prev) => {
        const updated = prev.map((msg) =>
          msg.id === botMessageId
            ? {
                ...msg,
                content: `NovaMatch could not complete: ${err.message}`,
                streaming: false
              }
            : msg
        );
        saveMessageHistory(updated);
        return updated;
      });
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const sendMessageInternal = async (messageText) => {
    if (!messageText || isLoading) {
      return;
    }

    if (isNovaMatchCommand(messageText)) {
      await runNovaMatchAnalysis(messageText);
      return;
    }

    if (chatEntitlementBlocked) {
      setShowSubscriptionPaywall(true);
      return;
    }

    setLastUserMessage(messageText);
    addMessage(messageText, 'user');
    setInputValue('');
    setSuggestions([]);
    setIsLoading(true);
    setIsTyping(true);

    // Create a placeholder bot message that will be updated with streaming content
    const botMessageId = Date.now() + 1; // +1 to ensure it's different from user message
    const timestamp = new Date().toISOString();
    const botMessage = {
      id: botMessageId,
      content: '',
      sender: 'bot',
      timestamp,
      streaming: true
    };
    
    setMessages(prev => {
      const updated = [...prev, botMessage];
      // DON'T save to localStorage yet - wait until streaming completes
      return updated;
    });

    try {
      const payload = { sessionId };
      if (API_URL.includes('/chat')) {
        payload.message = messageText;
      } else {
        payload.question = messageText;
      }
      saveLastKpiQuestion(sessionId, messageText);
      payload.kpiHistory = buildKpiHistory(sessionId, messages);

      // Include companyId for tenant-based metrics tracking
      // For superadmins: use selected tenant's companyId if one is selected
      // For regular admins: use their own companyId
      if (isSuperAdmin && selectedTenant) {
        payload.companyId = selectedTenant;
      } else if (currentUser?.companyId) {
        payload.companyId = currentUser.companyId;
      }

      // Include auth token if available
      const headers = {
        'Content-Type': 'application/json',
      };
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Check if streaming endpoint is available
      const streamingUrl = API_URL.replace('/answer', '/answer-stream');
      
      const response = await fetch(streamingUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errBody = null;
        try {
          errBody = await response.json();
        } catch {
          /* non-JSON */
        }
        if (errBody?.code === 'SUBSCRIPTION_REQUIRED' || errBody?.requiresPaywall) {
          setShowSubscriptionPaywall(true);
        }
        throw new Error(errBody?.message || `HTTP error! status: ${response.status}`);
      }

      // Check if response is streaming (SSE)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/event-stream')) {
        // Handle streaming response
        let streamedContent = '';
        let streamedSuggestions = [];
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'chunk' && data.content) {
                  streamedContent += data.content;
                  
                  // Update the bot message with streaming content
                  setMessages(prev => {
                    const updated = prev.map(msg => 
                      msg.id === botMessageId 
                        ? { ...msg, content: stripTopicTags(streamedContent), streaming: true }
                        : msg
                    );
                    // Save to storage periodically (throttled)
                    return updated;
                  });
                  
                  // Smooth scroll to keep content visible
                  if (messagesContainerRef.current) {
                    const container = messagesContainerRef.current;
                    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
                    if (isNearBottom) {
                      // Only auto-scroll if user is already near the bottom
                      requestAnimationFrame(() => {
                        container.scrollTop = container.scrollHeight;
                      });
                    }
                  }
                } else if (data.type === 'done') {
                  // Stream completed: the server sends the cleaned final answer
                  if (typeof data.answer === 'string' && data.answer.trim()) {
                    streamedContent = data.answer;
                  }
                  if (data.suggestions && Array.isArray(data.suggestions)) {
                    streamedSuggestions = data.suggestions;
                  }
                } else if (data.type === 'error') {
                  console.error('Streaming error:', data.error);
                  streamedContent += '\n\nSorry, I encountered an error. Please try again.';
                }
              } catch (parseError) {
                console.error('Failed to parse SSE data:', parseError);
              }
            }
          }
        }
        
        // Finalize the message - ensure sender is 'bot'
        setMessages(prev => {
          const updated = prev.map(msg => 
            msg.id === botMessageId 
              ? { ...msg, content: stripTopicTags(streamedContent), streaming: false, sender: 'bot' }
              : msg
          );
          saveMessageHistory(updated);
          return updated;
        });
        
        setIsTyping(false);
        
        // Set suggestions
        if (streamedSuggestions.length > 0) {
          const unique = Array.from(new Set(streamedSuggestions.map(s => (s || '').trim()))).filter(Boolean);
          setSuggestions(unique.slice(0, MAX_SUGGESTIONS));
        }
        
      } else {
        // Fallback to non-streaming response
        const data = await response.json();
        setIsTyping(false);
        
        const botText = data.answer || data.response || 'Sorry, I encountered an error. Please try again.';
        
        // Update the bot message - ensure sender is 'bot'
        setMessages(prev => {
          const updated = prev.map(msg => 
            msg.id === botMessageId 
              ? { ...msg, content: botText, streaming: false, sender: 'bot' }
              : msg
          );
          saveMessageHistory(updated);
          return updated;
        });
        
        if (data.suggestions && Array.isArray(data.suggestions)) {
          const unique = Array.from(new Set(data.suggestions.map(s => (s || '').trim()))).filter(Boolean);
          setSuggestions(unique.slice(0, MAX_SUGGESTIONS));
        }
      }

    } catch (error) {
      console.error('Chat API error:', error);
      setIsTyping(false);
      
      // Update the bot message with error - ensure sender is 'bot'
      setMessages(prev => {
        const updated = prev.map(msg => 
          msg.id === botMessageId 
            ? { ...msg, content: 'Sorry, I\'m having trouble connecting. Please try again later.', streaming: false, sender: 'bot' }
            : msg
        );
        saveMessageHistory(updated);
        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    const message = inputValue.trim();
    if (message && !isLoading) {
      await sendMessageInternal(message);
    }
  };

  const handleQuickAction = async (text) => {
    setInputValue(text);
    await new Promise(resolve => setTimeout(resolve, 0));
    const message = text.trim();
    if (message && !isLoading) {
      await sendMessageInternal(message);
    }
  };

  // Action links in answers: [Open PDF Tools](/tools/pdf) navigates, [Start the tour](nb:tour) runs a chat action.
  const handleInAppTarget = (target) => {
    if (target === 'nb:tour') startTour();
    else if (target === 'nb:feedback') setShowFeedback(true);
    else if (target === 'nb:demo') setHelpCenterTab('demo');
    else if (target === 'nb:whats-new') setHelpCenterTab('whats-new');
    else if (target === 'nb:help') setHelpCenterTab('topics');
    else if (target === 'nb:connect-novaerp') {
      connectNovaErp().catch((err) => {
        if (authService.isSessionExpiredError(err)) authService.redirectToLogin();
        else navigate('/apps');
      });
    }
    else if (target.startsWith('/')) navigate(target);
  };

  const handleMessagesClick = (e) => {
    const action = e.target.closest?.('[data-nb-target]');
    if (action) handleInAppTarget(action.getAttribute('data-nb-target'));
  };

  // Other pages open the chat with a help question or the feedback dialog (HelpMenu / FeedbackButton).
  const handledNavStateRef = useRef(null);
  useEffect(() => {
    const { helpQuestion, openFeedback } = location.state || {};
    if (!helpQuestion && !openFeedback) return;
    // Handle each navigation once (effects can run twice in development).
    if (handledNavStateRef.current === location.key) return;
    handledNavStateRef.current = location.key;
    navigate(location.pathname, { replace: true, state: null });
    if (openFeedback) setShowFeedback(true);
    if (helpQuestion) handleQuickAction(helpQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSuggestionClick = async (suggestion) => {
    setInputValue(suggestion);
    await new Promise(resolve => setTimeout(resolve, 0));
    const message = suggestion.trim();
    if (message && !isLoading) {
      await sendMessageInternal(message);
    }
  };

  const escalateToHR = () => {
    if (!hrContactEmail) {
      alert('HR contact email is not configured. Please contact your administrator to set up the HR contact email in the admin dashboard.');
      return;
    }
    const subject = encodeURIComponent('Assistance Request from NovaBot');
    const body = encodeURIComponent('Hi HR Team,\n\nI need assistance with an HR question that NovaBot could not resolve.\n\nThanks,');
    const mailto = `mailto:${hrContactEmail}?subject=${subject}&body=${body}`;
    window.open(mailto, '_blank');
  };

  const handleVote = async (vote, messageId) => {
    try {
      const message = messages.find(m => m.id === messageId);
      
      // Extract user info from currentUser if available
      const userId = currentUser?.id || 'anonymous';
      const userName = currentUser?.firstName && currentUser?.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`.trim()
        : currentUser?.email?.split('@')[0] || 'Anonymous User';
      const userEmail = currentUser?.email || '';
      const companyId = currentUser?.companyId || (isSuperAdmin && selectedTenant ? selectedTenant : null);

      const feedbackData = {
        vote,
        responseText: message?.content?.substring(0, 500) || '',
        messageId: messageId.toString(),
        sessionId,
        userId,
        userName,
        userEmail,
        companyId,
        timestamp: new Date().toISOString()
      };

      const headers = {
        'Content-Type': 'application/json',
      };
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(`${API_BASE_URL}/api/metrics/vote-feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify(feedbackData)
      });
    } catch (error) {
      console.error('Error sending vote feedback:', error);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleRetry = async (messageId) => {
    if (isLoading || !lastUserMessage) return;
    setMessages(prev => {
      const lastUserIndex = prev.findLastIndex(m => m.sender === 'user');
      if (lastUserIndex >= 0) {
        return prev.slice(0, lastUserIndex + 1);
      }
      return prev;
    });
    setInputValue(lastUserMessage);
    await new Promise(resolve => setTimeout(resolve, 0));
    await sendMessageInternal(lastUserMessage);
  };

  const handleStarClick = (rating) => {
    setStarRating(rating);
  };

  const handleReviewChange = (e) => {
    const text = e.target.value;
    const words = (text.trim().match(/\b\w+\b/g) || []);
    const wc = words.length;
    setWordCount(wc);
    if (wc > 50) {
      const limitedWords = words.slice(0, 50);
      setReviewText(limitedWords.join(' '));
    } else {
      setReviewText(text);
    }
  };

  const submitRating = async () => {
    if (!starRating) {
      setFeedbackStatus({ type: 'error', message: 'Please pick a star rating first.' });
      return;
    }

    setFeedbackStatus({ type: 'sending' });
    try {
      // Extract user info from currentUser if available
      const userId = currentUser?.id || 'anonymous';
      const userName = currentUser?.firstName && currentUser?.lastName
        ? `${currentUser.firstName} ${currentUser.lastName}`.trim()
        : currentUser?.email?.split('@')[0] || 'Anonymous User';
      const userEmail = currentUser?.email || '';
      const companyId = currentUser?.companyId || (isSuperAdmin && selectedTenant ? selectedTenant : null);

      const payload = {
        rating: starRating,
        review: reviewText.trim(),
        sessionId,
        userId,
        userName,
        userEmail,
        companyId,
        ts: Date.now()
      };

      const headers = {
        'Content-Type': 'application/json',
      };
      const token = authService.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/metrics/rating-review`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setFeedbackStatus({ type: 'sent' });
        setTimeout(() => {
          setShowFeedback(false);
          setFeedbackStatus(null);
          setStarRating(0);
          setReviewText('');
          setWordCount(0);
        }, 1800);
      } else {
        const data = await response.json().catch(() => ({}));
        setFeedbackStatus({ type: 'error', message: data.message || 'Could not send your feedback. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      setFeedbackStatus({ type: 'error', message: 'Could not send your feedback. Please check your connection and try again.' });
    }
  };

  const closeFeedback = () => {
    setShowFeedback(false);
    setFeedbackStatus(null);
  };

  const getCharCountClass = () => {
    const length = inputValue.length;
    if (length >= ERROR_THRESHOLD) return 'error';
    if (length >= WARNING_THRESHOLD) return 'warning';
    return '';
  };

  const persistCurrentSession = useCallback(() => {
    // Only chats where the user asked something are worth listing in Recent.
    if (!sessionId || !messages.some((m) => m.sender === 'user')) return;
    const title = deriveChatTitle(messages);
    saveSessionSnapshot(sessionId, {
      title,
      messages,
      updatedAt: Date.now()
    });
    const recents = loadRecentChats().filter((chat) => chat.id !== sessionId);
    recents.unshift({ id: sessionId, title, updatedAt: Date.now() });
    const trimmed = recents.slice(0, 12);
    saveRecentChats(trimmed);
    setRecentChats(trimmed);
  }, [sessionId, messages]);

  const resetChatState = useCallback(async () => {
    welcomeMessageShownRef.current = false;
    setMessages([]);
    setSuggestions([]);
    clearMessageHistory();
    await fetchWelcomeMessage();
  }, [fetchWelcomeMessage]);

  const startNewChat = async () => {
    persistCurrentSession();
    try {
      await fetch(`${API_BASE_URL}/api/chat/session/${sessionId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
    } catch (err) {
      console.warn('Error clearing backend session (non-critical):', err);
    }
    const newSessionId = generateNewSessionId();
    setSessionId(newSessionId);
    await resetChatState();
    if (isMobile) setShowMobileMenu(false);
  };

  const switchToSession = (targetSessionId) => {
    if (targetSessionId === sessionId) return;
    persistCurrentSession();
    const snapshot = loadSessionSnapshot(targetSessionId);
    if (!snapshot?.messages?.length) return;
    setStoredSessionId(targetSessionId);
    setSessionId(targetSessionId);
    setMessages(snapshot.messages);
    saveMessageHistory(snapshot.messages);
    welcomeMessageShownRef.current = snapshot.messages.some((msg) => msg.sender === 'bot');
    setSuggestions([]);
    if (isMobile) setShowMobileMenu(false);
  };

  const deleteRecentChat = async (chatId, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();

    if (!(await confirmDialog({
      title: 'Delete this chat?',
      message: 'It will be removed from your Recent list on this device.',
      confirmLabel: 'Delete chat',
      tone: 'danger',
    }))) return;

    removeSessionSnapshot(chatId);
    const remaining = loadRecentChats().filter((chat) => chat.id !== chatId);
    saveRecentChats(remaining);
    setRecentChats(remaining);

    try {
      await fetch(`${API_BASE_URL}/api/chat/session/${chatId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
    } catch (err) {
      console.warn('Error clearing backend session (non-critical):', err);
    }

    if (chatId === sessionId) {
      const newSessionId = generateNewSessionId();
      setSessionId(newSessionId);
      await resetChatState();
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      persistCurrentSession();
    }
  }, [messages, persistCurrentSession]);

  const chatTitle = deriveChatTitle(messages);
  // Until the user asks something, show the welcome screen instead of the lone greeting bubble.
  const isFreshChat = !messages.some((m) => m.sender === 'user');

  const clearChat = async () => {
    if (await confirmDialog({
      title: 'Clear this chat?',
      message: 'All messages in this conversation will be removed.',
      confirmLabel: 'Clear chat',
      tone: 'danger',
    })) {
      const oldSessionId = sessionId;
      
      // 1. Clear backend session memory (if session exists)
      try {
        const deleteResponse = await fetch(`${API_BASE_URL}/api/chat/session/${oldSessionId}`, {
          method: 'DELETE',
          headers: authService.getAuthHeaders()
        });
        if (deleteResponse.ok) {
          // Session cleared successfully
        } else if (deleteResponse.status !== 404) {
          // 404 is OK - session might not exist yet
          console.warn('Failed to clear backend session:', deleteResponse.status);
        }
      } catch (err) {
        // Non-critical error - continue with frontend clearing
        console.warn('Error clearing backend session (non-critical):', err);
      }
      
      // 2. Generate new session ID and update state
      const newSessionId = generateNewSessionId();
      setSessionId(newSessionId);
      
      // 3. Clear frontend localStorage
      clearMessageHistory();
      
      // 4. Clear React state
      setMessages([]);
      welcomeMessageShownRef.current = false; // Reset flag so welcome message can be shown again
      
      // 5. Refetch welcome message to get the latest version with cache-busting
      // The useEffect will automatically show the welcome message when messages.length becomes 0
      const timestamp = new Date().getTime();
      const url = `${API_BASE_URL}/api/tenant/welcome-message${isSuperAdmin && selectedTenant ? `?companyId=${selectedTenant}&_t=${timestamp}` : `?_t=${timestamp}`}`;
      try {
        const response = await fetch(url, {
          headers: authService.getAuthHeaders(),
          cache: 'no-cache'
        });
        const data = await response.json();
        // Update state with fetched message - useEffect will handle showing it
        if (data.status === 'success' && data.data?.welcomeMessage) {
          setWelcomeMessage(data.data.welcomeMessage);
        } else {
          setWelcomeMessage(null);
        }
        // Don't call addMessage here - let the useEffect handle it to prevent duplicates
      } catch (err) {
        console.error('Error fetching welcome message:', err);
        // Set welcomeMessage to null to use default - useEffect will handle showing it
        setWelcomeMessage(null);
      }
    }
  };

  const handleLogout = async () => {
    if (await confirmDialog({
      title: 'Sign out of NovaBotX?',
      message: 'Your chats stay saved. Next time you sign in you\u2019ll need your password and a 2FA code.',
      confirmLabel: 'Sign out',
      tone: 'logout',
    })) {
      await authService.logout();
      navigate('/login');
    }
  };

  const handleTenantSwitch = async (newTenant, previousTenant, revertSelect) => {
    if (!(await confirmDialog({
      title: 'Switch organisation?',
      message: 'Switching tenant clears your current chat and starts a new one for that organisation.',
      confirmLabel: 'Switch',
    }))) {
      if (revertSelect) revertSelect(previousTenant || '');
      return;
    }

    const oldSessionId = sessionId;

    try {
      const deleteResponse = await fetch(`${API_BASE_URL}/api/chat/session/${oldSessionId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders()
      });
      if (!deleteResponse.ok && deleteResponse.status !== 404) {
        console.warn('Failed to clear backend session:', deleteResponse.status);
      }
    } catch (err) {
      console.warn('Error clearing backend session (non-critical):', err);
    }

    const newSessionId = generateNewSessionId();
    setSessionId(newSessionId);
    clearMessageHistory();
    setMessages([]);
    welcomeMessageShownRef.current = false;
    setSelectedTenant(newTenant);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tenant/welcome-message${newTenant ? `?companyId=${newTenant}` : ''}`, {
        headers: authService.getAuthHeaders(),
        cache: 'no-cache'
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.welcomeMessage) {
        setWelcomeMessage(data.data.welcomeMessage);
      } else {
        setWelcomeMessage(null);
      }
    } catch (err) {
      console.error('Error fetching welcome message:', err);
      setWelcomeMessage(null);
    }
  };

  const sidebarRecentItems = (() => {
    // Hide empty chats saved by older versions; the current chat is added back below.
    const items = recentChats.filter((chat) => chat.id === sessionId || chat.title !== EMPTY_CHAT_TITLE);
    const currentIndex = items.findIndex((chat) => chat.id === sessionId);
    if (currentIndex === -1 && messages.length > 0) {
      items.unshift({ id: sessionId, title: chatTitle, updatedAt: Date.now() });
    } else if (currentIndex !== -1) {
      items[currentIndex] = { ...items[currentIndex], title: chatTitle };
    }
    return items.slice(0, 12);
  })();
  const visibleSidebarRecentItems = showAllRecentChats
    ? sidebarRecentItems
    : sidebarRecentItems.slice(0, 4);

  // The sidebar steps only exist on desktop with the sidebar expanded.
  const chatTourSteps = isMobile
    ? CHAT_TOUR_STEPS.filter((s) => s.target === 'chat-messages' || s.target === 'chat-composer')
    : CHAT_TOUR_STEPS;

  // Open the sidebar sections the tour points at in the same render the tour starts,
  // so TourMode finds them when it checks which steps can be shown.
  const startTour = () => {
    if (!isMobile) {
      setSidebarCollapsed(false);
      setShowQuickActionsExpanded(true);
    }
    beginTour();
  };


  const renderBrand = (className = '', { showName = true, compact = false } = {}) => (
    <div className={`sidebar-brand-mark ${compact ? 'is-compact' : ''} ${className}`.trim()}>
      <div className="sidebar-logo-box">
        {tenantLogoUrl ? (
          <img
            src={tenantLogoUrl.startsWith('http') ? tenantLogoUrl : `${API_BASE_URL}${tenantLogoUrl}`}
            alt="Company Logo"
            className="sidebar-logo"
            onError={(e) => {
              e.target.src = `${import.meta.env.BASE_URL}assets/novalogo.png`;
              e.target.onerror = null;
            }}
          />
        ) : (
          <img
            src={`${import.meta.env.BASE_URL}assets/novalogo.png`}
            alt="NovaLink"
            className="sidebar-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>
      {showName && <span className="sidebar-brand-name">NovaBotX</span>}
    </div>
  );

  const renderSidebarContent = () => (
    <>
      <div className={`sidebar-brand ${sidebarCollapsed ? 'is-collapsed' : ''}`}>
        <div className="sidebar-brand-top">
          {!sidebarCollapsed && <NovaBotXLogo size={36} withWordmark tagline="AI knowledge assistant" />}
          {!isMobile && (
            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </button>
          )}
          {isMobile && (
            <button type="button" className="sidebar-collapse-btn" onClick={() => setShowMobileMenu(false)} aria-label="Close menu">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        {!sidebarCollapsed && renderBrand('sidebar-brand-stack', { showName: false })}
      </div>

      {sidebarCollapsed && !isMobile ? (
        <div className="sidebar-icon-rail" aria-label="Sidebar shortcuts">
          <NovaBotXLogo size={40} className="sidebar-rail-logo" />
          <button type="button" className="sidebar-icon-btn sidebar-icon-btn-primary" onClick={startNewChat} title="New chat" aria-label="New chat">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button type="button" className="sidebar-icon-btn" onClick={() => navigate('/apps')} title="My Apps" aria-label="My Apps">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1.5" />
              <rect x="14" y="3" width="7" height="7" rx="1.5" />
              <rect x="3" y="14" width="7" height="7" rx="1.5" />
              <path d="M17.5 14l3.5 3.5-3.5 3.5-3.5-3.5z" fill="currentColor" stroke="none" />
            </svg>
          </button>
          <button type="button" className="sidebar-icon-btn" onClick={() => setSidebarCollapsed(false)} title="Recent chats" aria-label="Recent chats">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </button>
          <button type="button" className="sidebar-icon-btn" onClick={() => { setSidebarCollapsed(false); setShowQuickActionsExpanded(true); }} title="Quick actions" aria-label="Quick actions">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </button>
        </div>
      ) : (
        !sidebarCollapsed && (
          <>
            <button type="button" className="sidebar-new-chat-btn" onClick={startNewChat} data-tour="chat-new">
              <FiPlus /> New chat
            </button>

            <button type="button" className="sidebar-my-apps-btn" onClick={() => navigate('/apps')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1.5" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" />
                <path d="M17.5 14l3.5 3.5-3.5 3.5-3.5-3.5z" fill="currentColor" stroke="none" />
              </svg>
              My Apps
            </button>

            <div className="sidebar-section" data-tour="chat-recent">
              <div className="sidebar-section-label">RECENT</div>
              <div className="sidebar-recent-list">
                {sidebarRecentItems.length === 0 ? (
                  <div className="sidebar-recent-empty">No recent chats yet</div>
                ) : (
                  visibleSidebarRecentItems.map((chat) => (
                    <div
                      key={chat.id}
                      className={`sidebar-recent-row ${chat.id === sessionId ? 'active' : ''}`}
                    >
                      <button
                        type="button"
                        className="sidebar-recent-item"
                        onClick={() => switchToSession(chat.id)}
                        title={chat.title}
                      >
                        <FiMessageSquare className="sidebar-recent-icon" aria-hidden="true" />
                        <span className="sidebar-recent-text">
                          <span className="sidebar-recent-title">
                            {chat.title === EMPTY_CHAT_TITLE ? 'New conversation' : chat.title}
                          </span>
                          <span className="sidebar-recent-time">
                            {chat.id === sessionId && chat.title === EMPTY_CHAT_TITLE ? 'Ask a question to start' : formatRecentTime(chat.updatedAt)}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="sidebar-recent-delete"
                        onClick={(e) => deleteRecentChat(chat.id, e)}
                        aria-label={`Delete ${chat.title}`}
                        title="Delete chat"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>
              {sidebarRecentItems.length > 4 && (
                <button
                  type="button"
                  className="sidebar-recent-view-all"
                  onClick={() => setShowAllRecentChats(!showAllRecentChats)}
                >
                  {showAllRecentChats ? 'Show less' : `View all (${sidebarRecentItems.length})`}
                </button>
              )}
            </div>

            <div className="sidebar-section sidebar-quick-actions-section" data-tour="chat-quick">
              <button
                type="button"
                className="sidebar-section-toggle"
                onClick={() => setShowQuickActionsExpanded(!showQuickActionsExpanded)}
                aria-expanded={showQuickActionsExpanded}
              >
                <span className="sidebar-toggle-icon">{showQuickActionsExpanded ? <FiChevronDown /> : <FiChevronRight />}</span>
                QUICK ACTIONS
              </button>
              {showQuickActionsExpanded && (
                <div className="sidebar-quick-actions-list">
                  <button type="button" className="sidebar-quick-action" onClick={() => handleQuickAction('What is our leave policy?')}>
                    Leave Policy
                  </button>
                  <button type="button" className="sidebar-quick-action" onClick={() => handleQuickAction('What benefits are offered?')}>
                    Benefits
                  </button>
                  <button type="button" className="sidebar-quick-action" onClick={() => handleQuickAction('How do I submit expenses?')}>
                    Expenses
                  </button>
                  <button type="button" className="sidebar-quick-action" onClick={() => handleQuickAction('Who do I contact for payroll?')}>
                    Payroll
                  </button>
                </div>
              )}
            </div>
          </>
        )
      )}

      <div className="sidebar-spacer" />

      {sidebarCollapsed && !isMobile ? (
        <div className="sidebar-icon-rail sidebar-icon-rail-bottom" aria-label="Sidebar actions">
          {isAdmin && (
            <button type="button" className="sidebar-icon-btn" onClick={() => navigate('/admin')} title="Admin dashboard" aria-label="Admin dashboard">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="sidebar-icon-btn"
            onClick={() => {
              setSidebarCollapsed(false);
              setShowMoreActions(true);
            }}
            title="More actions"
            aria-label="More actions"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="5" cy="12" r="1" />
              <circle cx="12" cy="12" r="1" />
              <circle cx="19" cy="12" r="1" />
            </svg>
          </button>
          <button type="button" className="sidebar-icon-btn" onClick={handleLogout} title="Logout" aria-label="Logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      ) : (
        !sidebarCollapsed && (
          <div className="sidebar-actions-panel">
            {isSuperAdmin && (
              <div className="sidebar-tenant-select">
                <select
                  value={selectedTenant || ''}
                  onChange={(e) => {
                    const selectEl = e.target;
                    handleTenantSwitch(selectEl.value, selectedTenant, (value) => {
                      selectEl.value = value;
                    });
                  }}
                  title="Select Tenant to Chat With"
                >
                  <option value="">System (No Tenant)</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.companyId} value={tenant.companyId}>
                      {tenant.companyName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {isAdmin && (
              <button type="button" className="sidebar-action-item" onClick={() => navigate('/admin')}>
                <span className="sidebar-action-icon" aria-hidden="true"><FiGrid /></span>
                Admin dashboard
              </button>
            )}
            <button
              type="button"
              className="sidebar-action-item sidebar-more-toggle"
              onClick={() => setShowMoreActions(!showMoreActions)}
              aria-expanded={showMoreActions}
            >
              <span className="sidebar-action-icon" aria-hidden="true"><FiMoreHorizontal /></span>
              More
              <span className="sidebar-more-chevron" aria-hidden="true">{showMoreActions ? <FiChevronUp /> : <FiChevronDown />}</span>
            </button>
            {showMoreActions && (
              <div className="sidebar-more-menu">
                <button type="button" className="sidebar-action-item" onClick={() => setShowFeedback(!showFeedback)}>
                  <span className="sidebar-action-icon" aria-hidden="true"><FiMessageSquare /></span>
                  Feedback
                </button>
                <button type="button" className="sidebar-action-item" onClick={escalateToHR}>
                  <span className="sidebar-action-icon" aria-hidden="true"><FiHeadphones /></span>
                  Connect to HR
                </button>
                {financeEmail && (
                  <button type="button" className="sidebar-action-item" onClick={escalateToFinance}>
                    <span className="sidebar-action-icon" aria-hidden="true"><FiDollarSign /></span>
                    Connect to Finance
                  </button>
                )}
                <button type="button" className="sidebar-action-item" onClick={clearChat}>
                  <span className="sidebar-action-icon" aria-hidden="true"><FiTrash2 /></span>
                  Clear chat
                </button>
              </div>
            )}
          </div>
        )
      )}

      <div className={`sidebar-user-profile ${sidebarCollapsed ? 'is-collapsed' : ''}`}>
        <div className="sidebar-user-avatar" title={getUserDisplayName(currentUser)}>{getUserInitials(currentUser)}</div>
        {!sidebarCollapsed && (
          <>
            <div className="sidebar-user-meta">
              <div className="sidebar-user-name">{getUserDisplayName(currentUser)}</div>
              <div className="sidebar-user-role">{formatRoleLabel(currentUser?.role)}</div>
            </div>
            <button type="button" className="sidebar-logout-btn" onClick={handleLogout} aria-label="Logout">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </>
        )}
      </div>
    </>
  );

  return (
    <div className={`chat-page ${isMobile ? 'mobile-view' : ''} ${isTablet ? 'tablet-view' : ''}`}>
      <div className="chat-orb chat-orb-1" aria-hidden="true" />
      <div className="chat-orb chat-orb-2" aria-hidden="true" />
      <div className={`chat-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {!isMobile && (
          <aside className={`chat-sidebar ${sidebarCollapsed ? 'is-collapsed' : ''}`}>
            {renderSidebarContent()}
          </aside>
        )}

        {isMobile && showMobileMenu && (
          <>
            <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)} />
            <aside className="chat-sidebar chat-sidebar-mobile">
              {renderSidebarContent()}
            </aside>
          </>
        )}

        <main className="chat-main">
          <div className="chat-aurora" aria-hidden="true"><span /><span /><span /></div>
          <header className="chat-conversation-header">
            {isMobile && (
              <button
                type="button"
                className="mobile-menu-btn"
                onClick={() => setShowMobileMenu(true)}
                aria-label="Open menu"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            )}
            <h2 className="chat-conversation-title">{chatTitle}</h2>
            <span className="chat-header-status">NovaBotX online</span>
            <div className="chat-header-actions">
              <HelpMenu
                onTour={startTour}
                onAsk={handleQuickAction}
                onFeedback={() => setShowFeedback(true)}
                onOpenCenter={setHelpCenterTab}
              />
              <FeedbackButton onClick={() => setShowFeedback(true)} />
              <TourButton onClick={startTour} />
              <ThemeToggle compact />
            </div>
          </header>

          <div className="chat-trial-banner-wrap">
            <TrialBanner
              trial={trialInfo}
              compact
              onViewPricing={() => setShowSubscriptionPaywall(true)}
            />
          </div>


          <div className="chat-messages" ref={messagesContainerRef} data-tour="chat-messages" onClick={handleMessagesClick}>
        {isFreshChat && (
          <WelcomeHero
            firstName={getFirstName(currentUser)}
            welcomeText={messages.find((m) => m.sender === 'bot')?.content}
            onPrompt={handleQuickAction}
          />
        )}
        {!isFreshChat && messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onVote={handleVote}
            onCopy={handleCopy}
            onRetry={handleRetry}
          />
        ))}
        
        {!isFreshChat && suggestions.length > 0 && (
          <Suggestions suggestions={suggestions} onClick={handleSuggestionClick} />
        )}
      </div>

      {showFeedback && (
        <FeedbackPanel
          starRating={starRating}
          reviewText={reviewText}
          wordCount={wordCount}
          onStarClick={handleStarClick}
          onReviewChange={handleReviewChange}
          onSubmit={submitRating}
          onClose={closeFeedback}
          status={feedbackStatus}
        />
      )}

      {helpCenterTab && (
        <HelpCenter
          tab={helpCenterTab}
          onTabChange={setHelpCenterTab}
          onAsk={handleQuickAction}
          onClose={() => setHelpCenterTab(null)}
        />
      )}

      {showSubscriptionPaywall && (
        <SubscriptionPaywall
          onClose={() => setShowSubscriptionPaywall(false)}
        />
      )}

      {showUpload && (
        <div className="upload-modal-overlay" onClick={() => setShowUpload(false)}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="upload-modal-close" 
              onClick={() => setShowUpload(false)}
              aria-label="Close upload"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <KnowledgeUpload 
              onUploadComplete={() => {
                setShowUpload(false);
                // Optionally refresh or show success message
              }} 
            />
          </div>
        </div>
      )}

      
      <div className="chat-input-container" data-tour="chat-composer">
        <div className="novamatch-attach-bar">
          <input
            ref={novamatchResumeInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.rtf,application/pdf"
            hidden
            onChange={(e) => setNovamatchResume(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="novamatch-attach-btn"
            onClick={() => novamatchResumeInputRef.current?.click()}
            title="Attach resume for #novamatch"
          >
            {novamatchResume ? `Resume: ${novamatchResume.name}` : '+ Resume'}
          </button>
          {novamatchResume && (
            <button
              type="button"
              className="novamatch-attach-clear"
              onClick={() => {
                setNovamatchResume(null);
                if (novamatchResumeInputRef.current) novamatchResumeInputRef.current.value = '';
              }}
            >
              Clear
            </button>
          )}
          <span className="novamatch-hint">
            Attach resume, paste JD in the box, end with <code>#novamatch</code>
          </span>
        </div>
        <div className="chat-input-wrapper">
          {isAdmin ? (
            <button
              className="chat-upload-icon chat-attach-btn"
              onClick={() => setShowUpload(!showUpload)}
              aria-label="Attach document"
              title="Upload document (Admin only)"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="chat-upload-icon chat-attach-btn"
              onClick={() => novamatchResumeInputRef.current?.click()}
              aria-label="Attach resume"
              title="Attach resume for #novamatch"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}
          <textarea
            ref={textareaRef}
            className="chat-textarea"
            placeholder={
              chatEntitlementBlocked
                ? 'Subscribe or renew access to send messages…'
                : 'Ask anything…'
            }
            rows="1"
            maxLength={isNovaMatchCommand(inputValue) ? 30000 : MAX_MESSAGE_LENGTH}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={chatEntitlementBlocked}
          />
          <button
            className="chat-send"
            onClick={sendMessage}
            disabled={isLoading || chatEntitlementBlocked}
            aria-label="Send message"
            title="Send message"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
        <div className="chat-composer-foot">
          <span className="chat-composer-hint"><kbd>Enter</kbd> to send · <kbd>Shift</kbd> + <kbd>Enter</kbd> for a new line</span>
          <div className={`char-counter ${getCharCountClass()}`}>
            <span className="char-count">{inputValue.length}</span>/{isNovaMatchCommand(inputValue) ? 30000 : MAX_MESSAGE_LENGTH}
          </div>
        </div>
      </div>
        </main>
      </div>
      {touring && <TourMode steps={chatTourSteps} topOffset={8} onClose={endTour} />}
    </div>
  );
}

function WelcomeHero({ firstName, welcomeText, onPrompt }) {
  return (
    <section className="chat-welcome" aria-label="Welcome">
      <div className="chat-welcome-orb" aria-hidden="true">
        <span className="chat-welcome-ring" />
        <span className="chat-welcome-ring is-outer" />
        <span className="chat-welcome-core"><FiCpu /></span>
      </div>
      <h1 className="chat-welcome-title">
        {getGreeting()}{firstName && <>, <span className="chat-welcome-name">{firstName}</span></>}
      </h1>
      {welcomeText && <p className="chat-welcome-text">{welcomeText.replace(/\n{2,}/g, '\n')}</p>}
      <ul className="chat-welcome-pills">
        <li><FiFileText /> Answers from your company documents</li>
        <li><FiCheckCircle /> Every answer shows its source</li>
        <li><FiLock /> Private to your organisation</li>
      </ul>
      <div className="chat-prompt-grid">
        {STARTER_PROMPTS.map(({ icon: Icon, title, question }, i) => (
          <button
            key={title}
            type="button"
            className="chat-prompt-card"
            style={{ '--i': i }}
            onClick={() => onPrompt(question)}
          >
            <span className="chat-prompt-icon"><Icon /></span>
            <span className="chat-prompt-body">
              <span className="chat-prompt-title">{title}</span>
              <span className="chat-prompt-question">{question}</span>
            </span>
            <FiArrowUpRight className="chat-prompt-arrow" aria-hidden="true" />
          </button>
        ))}
      </div>
    </section>
  );
}

function MessageBubble({ message, onVote, onCopy, onRetry }) {
  const [vote, setVote] = useState('');

  const handleVoteClick = (newVote) => {
    const finalVote = vote === newVote ? '' : newVote;
    setVote(finalVote);
    if (finalVote !== vote) {
      onVote(finalVote, message.id);
    }
  };

  const handleCopyClick = async () => {
    await onCopy(message.content);
  };

  if (message.sender === 'bot') {
    return (
      <div className={`chat-message bot fade-in`}>
        <div className="message-bubble bot">
          <div className="bot-meta">
            <span className="bot-avatar" aria-hidden="true"><FiCpu /></span>
            <span className="bot-title">NovaBotX</span>
            {message.streaming && <span className="streaming-indicator">●</span>}
          </div>
          <div 
            className="bot-text"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
          />
          {message.streaming && message.content && (
            <div className="streaming-cursor">▌</div>
          )}
          {message.novamatchDownloadUrl && !message.streaming && (
            <div className="novamatch-download-row">
              <a
                className="novamatch-download-btn"
                href={message.novamatchDownloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const headers = { ...authService.getAuthHeaders() };
                    delete headers['Content-Type'];
                    const res = await fetch(message.novamatchDownloadUrl, { headers });
                    if (!res.ok) throw new Error('Download failed');
                    const blob = await res.blob();
                    const ext = (res.headers.get('Content-Type') || '').includes('pdf') ? 'pdf' : 'html';
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `novamatch-report.${ext}`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    alert(err.message || 'Could not download report');
                  }
                }}
              >
                {message.novamatchHasPdf === false ? 'Download report' : 'Download PDF report'}
              </a>
            </div>
          )}
        </div>
        <div className="msg-actions">
          <button className={`act-btn act-copy ${vote === 'copy' ? 'selected' : ''}`} onClick={handleCopyClick} title="Copy" aria-label="Copy">
            <FiCopy />
          </button>
          <button className={`act-btn act-up ${vote === 'up' ? 'selected' : ''}`} onClick={() => handleVoteClick('up')} title="Upvote" aria-label="Upvote">
            <FiThumbsUp />
          </button>
          <button className={`act-btn act-down ${vote === 'down' ? 'selected' : ''}`} onClick={() => handleVoteClick('down')} title="Downvote" aria-label="Downvote">
            <FiThumbsDown />
          </button>
          <button className="act-btn act-retry" onClick={() => onRetry(message.id)} title="Retry" aria-label="Retry">
            <FiRefreshCw />
          </button>
        </div>
        <div className="message-time bot">
          {formatTime(new Date(message.timestamp))}
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-message user fade-in`}>
      <div className="message-bubble user">
        {message.content}
      </div>
      <div className="message-time user">
        {formatTime(new Date(message.timestamp))}
      </div>
    </div>
  );
}

function Suggestions({ suggestions, onClick }) {
  return (
    <div className="suggestions">
      <div className="suggestions-heading">You can also ask:</div>
      <div className="suggestions-chips">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            className="chip"
            onClick={() => onClick(suggestion)}
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
}

const RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

function FeedbackPanel({ starRating, reviewText, wordCount, onStarClick, onReviewChange, onSubmit, onClose, status }) {
  const [hovered, setHovered] = useState(0);
  const shown = hovered || starRating;
  const sending = status?.type === 'sending';

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="feedback-overlay" onClick={onClose}>
      <div
        className="feedback-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-title"
        onClick={(e) => e.stopPropagation()}
      >
        {status?.type === 'sent' ? (
          <div className="feedback-thanks" role="status">
            <span className="feedback-thanks-icon"><FiCheck /></span>
            <h3>Thanks for your feedback!</h3>
            <p>It helps us make NovaBotX better for everyone.</p>
          </div>
        ) : (
          <>
            <div className="feedback-head">
              <span className="feedback-icon" aria-hidden="true"><FiMessageSquare /></span>
              <div>
                <h3 id="feedback-title">How was this chat?</h3>
                <p>Your rating goes to your NovaBotX admins.</p>
              </div>
              <button type="button" className="feedback-close" onClick={onClose} aria-label="Close feedback">
                <FiX />
              </button>
            </div>

            <div className="feedback-stars" role="radiogroup" aria-label="Rating" onMouseLeave={() => setHovered(0)}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  role="radio"
                  aria-checked={starRating === star}
                  aria-label={`${star} star${star > 1 ? 's' : ''}: ${RATING_LABELS[star]}`}
                  className={`feedback-star ${star <= shown ? 'is-on' : ''} ${star === starRating ? 'is-picked' : ''}`}
                  onMouseEnter={() => setHovered(star)}
                  onClick={() => onStarClick(star)}
                >
                  <FiStar />
                </button>
              ))}
              <span className="feedback-rating-label">{shown ? RATING_LABELS[shown] : 'Tap a star'}</span>
            </div>

            <label className="feedback-label" htmlFor="nb-review">Tell us more <span>(optional)</span></label>
            <textarea
              id="nb-review"
              className="feedback-textarea"
              rows="3"
              placeholder="What worked well? What could be better?"
              value={reviewText}
              onChange={onReviewChange}
            />
            <div className="feedback-meta">
              {status?.type === 'error'
                ? <span className="feedback-error" role="alert"><FiAlertCircle /> {status.message}</span>
                : <span />}
              <span className={wordCount >= 50 ? 'is-limit' : ''}>{wordCount}/50 words</span>
            </div>

            <div className="feedback-actions">
              <button type="button" className="feedback-btn" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="feedback-btn is-primary"
                onClick={onSubmit}
                disabled={!starRating || sending}
              >
                {sending ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChatPage;

